import { Alert, AlertDescription, Button, Card, CardContent, Spinner } from '@design-system';
import { AlertCircle, ArrowLeft, BriefcaseBusiness, Mail } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClientUserMenu } from '@client/components/ClientUserMenu';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import {
  AdvisorPhoneVerificationFlow,
  AdvisorSignupCardHeader,
} from '@client/components/AdvisorPhoneVerificationFlow';
import { useClientSignOut } from '@client/components/hooks/use-sign-out';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { clearRoleOverride, useRoleSwitch } from '@client/hooks/useRoleSwitch';
import type { API } from '@client/api/types';
import { findPilotProgramBySignupIneligibleReason } from '@shared';
import { useAdvisorAccountActivation } from './use-advisor-account-activation';

type ActivationNavigationState = {
  advisorActivationConfirmed?: boolean;
  advisorActivationPreflight?: API.AdvisorAccountActivationPreflight;
} | null;

export function AdvisorAccountActivationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = location.state as ActivationNavigationState;
  const emailOnlyOverride =
    new URLSearchParams(location.search).get('override_connection') === 'email';
  const wasConfirmed = navigationState?.advisorActivationConfirmed === true || emailOnlyOverride;
  const returnToClientHomeBeforeActivation = useCallback(() => {
    clearRoleOverride();
    navigate('/client/home', { replace: true });
  }, [navigate]);

  useReturnToClientHomeWhenUnconfirmed(wasConfirmed, returnToClientHomeBeforeActivation);

  if (!wasConfirmed) return null;

  return (
    <ConfirmedAdvisorAccountActivationPage
      navigationState={navigationState ?? {}}
      emailOnlyOverride={emailOnlyOverride}
      returnToClientHomeBeforeActivation={returnToClientHomeBeforeActivation}
    />
  );
}

function useReturnToClientHomeWhenUnconfirmed(
  wasConfirmed: boolean,
  returnToClientHome: () => void,
) {
  useEffect(() => {
    if (!wasConfirmed) returnToClientHome();
  }, [returnToClientHome, wasConfirmed]);
}

function ConfirmedAdvisorAccountActivationPage({
  navigationState,
  emailOnlyOverride,
  returnToClientHomeBeforeActivation,
}: {
  navigationState: NonNullable<ActivationNavigationState>;
  emailOnlyOverride: boolean;
  returnToClientHomeBeforeActivation: () => void;
}) {
  const { switchRole } = useRoleSwitch();
  const handleSignOut = useClientSignOut();
  const { clerkEmail } = useClerkClientAuth();
  const activation = useAdvisorAccountActivation({
    startAfterConfirmation: true,
    initialPreflight: emailOnlyOverride
      ? null
      : (navigationState?.advisorActivationPreflight ?? null),
    emailOnlyOverride,
  });
  const blockedOrganizationName = findPilotProgramBySignupIneligibleReason(
    activation.preflight?.reason,
  )?.organizationDisplayName;
  const returnToClientHome = useCallback(() => {
    if (activation.activationSubmitted) {
      switchRole('client', '/client/home');
      return;
    }

    returnToClientHomeBeforeActivation();
  }, [activation.activationSubmitted, returnToClientHomeBeforeActivation, switchRole]);

  const email = activation.preflight?.email ?? clerkEmail ?? '';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8">
        <WaterlilyLogo className="h-6" />
        <ClientUserMenu
          handleClickSignOut={handleSignOut}
          clientEmail={email}
          onNavigateHome={returnToClientHome}
        />
      </header>

      <main className="pt-18 flex flex-1 items-center justify-center px-4 pb-12 md:pt-20">
        <Card className="bg-background w-full max-w-md gap-0 overflow-hidden rounded-2xl border p-0 shadow-none">
          {activation.step === 'loading' && (
            <LoadingState message="Checking advisor eligibility…" />
          )}

          {activation.step === 'provisioning' && (
            <LoadingState message="Creating your advisor account…" />
          )}

          {activation.step === 'confirmation' && (
            <LoadingState message="Preparing advisor setup…" />
          )}

          {activation.step === 'phone' && (
            <AdvisorPhoneVerificationFlow
              step="phone"
              title="Add your phone number"
              description="We’ll text you a code to finish setting up your advisor account."
              onSubmit={event => {
                event.preventDefault();
                void activation.sendPhoneCode();
              }}
              submitLabel="Send phone code"
              isSubmitting={activation.isSubmitting}
              countryCode={activation.countryCode}
              phoneNumber={activation.phoneNumber}
              onCountryCodeChange={activation.updateCountryCode}
              onPhoneNumberChange={activation.updatePhoneNumber}
              autoFocus
              beforeFields={<VerifiedEmail email={email} />}
              error={activation.error ? <ErrorAlert message={activation.error} /> : undefined}
              afterSubmit={
                <p className="text-center text-xs text-gray-600">
                  Your advisor account won’t be created until your phone is verified.
                </p>
              }
              backAction={{ label: 'Back', onClick: returnToClientHome }}
            />
          )}

          {activation.step === 'verify' && (
            <AdvisorPhoneVerificationFlow
              step="verify"
              title="Enter the text code"
              description="We just sent a 6-digit code to your phone."
              onSubmit={event => {
                event.preventDefault();
                void activation.verifyPhoneCode();
              }}
              submitLabel="Verify and continue"
              isSubmitting={activation.isSubmitting}
              submitDisabled={activation.phoneCode.length !== 6}
              phoneNumber={`${activation.countryCode} ${activation.phoneNumber}`}
              phoneCode={activation.phoneCode}
              onPhoneCodeChange={activation.setPhoneCode}
              onResend={() => void activation.resendPhoneCode()}
              error={activation.error ? <ErrorAlert message={activation.error} /> : undefined}
              backAction={{
                label: 'Use a different number',
                onClick: activation.returnToPhone,
              }}
            />
          )}

          {activation.step === 'blocked' && (
            <>
              <AdvisorSignupCardHeader
                icon={<BriefcaseBusiness className="text-primary-dark size-5" />}
                eyebrow="Advisor setup"
                title="An invitation is required"
                description="This email is part of an advisor program with additional access requirements."
              />
              <CardContent className="space-y-5 p-6">
                <VerifiedEmail email={email} />
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertDescription>
                    {blockedOrganizationName
                      ? `${blockedOrganizationName} advisor accounts`
                      : 'Advisor accounts for this email'}{' '}
                    are currently available only by invitation to the pilot. Contact
                    hello@joinwaterlily.com to request access.
                  </AlertDescription>
                </Alert>
                <BackButton onClick={returnToClientHome}>Back to my plan</BackButton>
              </CardContent>
            </>
          )}

          {activation.step === 'error' && (
            <>
              <AdvisorSignupCardHeader
                icon={<AlertCircle className="size-5 text-red-600" />}
                eyebrow="Advisor setup"
                title="We couldn’t finish setup"
                description="Your personal plan has not been changed."
              />
              <CardContent className="space-y-5 p-6">
                <ErrorAlert message={activation.error ?? 'Please try again.'} />
                <Button
                  className="h-12 w-full"
                  onClick={() => void activation.retryFailedStep()}
                >
                  Try again
                </Button>
                <BackButton onClick={returnToClientHome}>Back to my plan</BackButton>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}

function VerifiedEmail({ email }: { email: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-green-100">
        <Mail className="size-4 text-green-700" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-600">Signed in as</p>
        <p className="truncate text-sm font-medium text-gray-950">{email}</p>
      </div>
      <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
        Verified
      </span>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex min-h-80 flex-col items-center justify-center gap-3 p-10 text-center">
      <Spinner className="text-primary size-8" />
      <p className="text-sm text-gray-700">{message}</p>
    </div>
  );
}

function ErrorAlert({ message }: { message: string }) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="size-4" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function BackButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 transition-colors hover:text-gray-900"
      onClick={onClick}
    >
      <ArrowLeft className="size-4" />
      {children}
    </button>
  );
}
